import { useState, useEffect } from "react";
import {
  formatNok,
  formatNumber,
  numberDisplaySx,
} from "libraries/formatNumber";

export type AnimatedCounterFormat = "integer" | "decimal" | "currency-nok";

type Props = {
  value: number;
  duration?: number;
  format?: AnimatedCounterFormat;
  fractionDigits?: number;
};

const COUNTER_START = 0;
const COUNTER_DURATION_MS = 2500;

const AnimatedCounter = ({
  value,
  duration = COUNTER_DURATION_MS,
  format = "integer",
  fractionDigits = 2,
}: Props) => {
  const [count, setCount] = useState(COUNTER_START);

  useEffect(() => {
    let startTime: number;
    let animationFrameId: number;

    const animate = (timestamp: number) => {
      if (!startTime) {
        startTime = timestamp;
      }
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const currentCount = COUNTER_START + (value - COUNTER_START) * progress;

      setCount(currentCount);

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrameId);
  }, [value, duration]);

  const formatValue = (current: number): string => {
    if (format === "currency-nok") {
      return formatNok(current);
    }
    if (format === "decimal") {
      return formatNumber(current, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });
    }
    return formatNumber(current);
  };

  return <span style={numberDisplaySx}>{formatValue(count)}</span>;
};

export default AnimatedCounter;
